
import {getImageOfSize, getImageAttributes} from "@/utils/getImagePaths.js";

// ToDo make this component responsive i.e. refresh triggers etc.

export default {
  name: "Timeline",
  props: {
    processArr: Array,
    activeIndex: Number,
    isMobileTimeline: Boolean
  },
  data() {
    return {
      oldActiveIndex: 0,
      selectedIndex: 0,
      indexToRemove: 0,
      listItemHeights: [],
      cumulativeHeights: [],
      totalHeight: 0,
      scrollTrigger: null,
      mobileActiveIndexes: [],
      oldMobileActiveIndexes: [],
      animations: new Map(), // store the animation instance
    };
  },
  computed: {
    lastListItem() {
      // return the last item in the $refs array
      return this.$refs.listItems[this.$refs.listItems.length - 1];
    },
    computedSelectedIndex() {
      if (this.isMobileTimeline) {
        return this.selectedIndex;
      } else {
        return null;
      }
    },
    isActiveArr() {
      if (this.isMobileTimeline) {
        return this.mobileActiveIndexes;
      } else {
        return [this.activeIndex];
      }
    }
  },
  watch: {
    mobileActiveIndexes(newVal) {
      // Check for additions
      if (newVal.length > this.oldMobileActiveIndexes.length) {
        this.triggerDropdownAnimation(this.mobileActiveIndexes[this.mobileActiveIndexes.length - 1]);
      }
      // Check for removals
      else if (newVal.length < this.oldMobileActiveIndexes.length) {
        this.triggerDropdownAnimation(this.oldMobileActiveIndexes[this.indexToRemove]);
      }

      this.oldMobileActiveIndexes = JSON.parse(JSON.stringify(newVal));
    },
    activeIndex(newIndex, oldIndex) {
      const newImage = this.$refs.imageItems[newIndex];
      this.oldActiveIndex = oldIndex;

      this.$gsap.fromTo(
        newImage,
        {y: newIndex > oldIndex ? "100%" : "-100%"},
        {
          y: "0%",
          duration: 0.5,
          ease: "power3.inOut" // or any other ease type you'd like to use
        }
      );
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setListItemHeights();
      this.setCumulativeHeights();
      this.initiateScrollAnimation();
    });
  },
  methods: {
    getImageOfSize,
    getImageAttributes,
    changeActiveIndex(i) {
      if (this.isMobileTimeline) {
        if (!this.mobileActiveIndexes.includes(i)) {
          this.mobileActiveIndexes.push(i);
        } else {
          let index = this.mobileActiveIndexes.indexOf(i);

          if (index !== -1) {
            this.indexToRemove = index;
            this.mobileActiveIndexes.splice(index, 1);
          }
        }
      } else {
        this.$emit('update-active-index', i + 1);
      }
    },
    triggerDropdownAnimation(i) {
      // If there is an existing animation for this panel, reverse it
      if (this.animations.has(i)) {
        this.animations.get(i).reverse();
        this.animations.delete(i); // Once reversed, remove the animation
        return;
      } else if (typeof i === 'undefined') {
        // Get the first (and only) key-value pair
        let entry = this.animations.entries().next().value;

        // Reverse the animation
        entry[1].reverse();

        // Delete the element using its key
        this.animations.delete(entry[0]);
        return;
      }

      let panel = this.$refs.panel[i];
      let naturalHeight = this.getNaturalHeight(panel);

      // Now animate from 0 to naturalHeight
      this.animations.set(i, this.$gsap.fromTo(panel,
        {height: '0px'},
        {
          height: `${naturalHeight}px`,
          duration: .35,
          onComplete: () => {
            this.$gsap.to(panel,
              {
                opacity: 1,
              }
            );
            this.setListItemHeights();
            this.setCumulativeHeights();
            this.scrollTrigger.refresh();
          },
          onReverseComplete: () => {
            this.$gsap.to(panel,
              {
                opacity: 0,
              }
            );
            this.setListItemHeights();
            this.setCumulativeHeights();
            this.scrollTrigger.refresh();
          }
        }
      ));
    },
    initiateScrollAnimation() {
      this.$nextTick(() => {
        this.scrollTrigger = this.$ScrollTrigger.create({
          trigger: this.$refs.container,
          start: "top center",
          endTrigger: this.lastListItem,
          scroller: null,
          end: "top-=22 center",
          pin: this.$refs.moreButton[0],
          // markers: true,
          onUpdate: (context) => {
            // Multiply the total height by the scroll progress to get the current scrollY equivalent
            let currentHeight = this.totalHeight * context.progress;

            // The following logic calculates which list item the current scrollY equivalent belongs to.
            let cumulativeHeight = 0;
            let index = this.listItemHeights.findIndex(height => {
              cumulativeHeight += height;
              return currentHeight < cumulativeHeight;
            });

            // If an item was found, update the activeIndex.
            // Ensure that index is a valid number before emitting the update event.
            if (!isNaN(index) && index !== -1) {
              // console.log(`The pin is within item ${index}`);

              if (index !== this.selectedIndex) {
                this.selectedIndex = index;
              }
            }
          },
        });
      });
    },
    setCumulativeHeights() {
      let cumulativeHeight = 0;
      const offset = this.$refs.listItems[0].getBoundingClientRect().top;
      this.cumulativeHeights = this.listItemHeights.map(height => {
        cumulativeHeight += height;
        return cumulativeHeight + offset;
      });
    },
    setListItemHeights() {
      this.listItemHeights = this.$refs.listItems.map((item, index) => {
        const style = window.getComputedStyle(item);
        const marginTop = parseFloat(style.marginTop);
        const marginBottom = parseFloat(style.marginBottom);

        if (index === this.processArr.length - 1) {
          return item.firstElementChild.offsetHeight + marginTop + marginBottom;
        }

        return item.offsetHeight + marginTop + marginBottom;
      });

      // Update totalHeight here, when listItemHeights are recalculated.
      this.totalHeight = this.listItemHeights.reduce((total, currentValue) => total + currentValue, 0);
    },
    getNaturalHeight(element) {
      // Save the current styles to restore later
      let oldPosition = element.style.position;
      let oldTop = element.style.top;
      let oldHeight = element.style.height;
      let oldVisibility = element.style.visibility;

      // Temporarily change the element's style to measure its natural height
      element.style.position = 'absolute';
      element.style.top = '-100%';
      element.style.visibility = 'hidden';
      element.style.height = 'auto';

      // Get the height
      let naturalHeight = element.offsetHeight;

      // Revert the styles back
      element.style.position = oldPosition;
      element.style.top = oldTop;
      element.style.height = oldHeight;
      element.style.visibility = oldVisibility;

      return naturalHeight;
    }
  }
}
